/*
 * PRS Product Gallery Vimeo Autoplay
 * -----------------------------------
 * Custom functionality by Proof-Reading-Service.com
 *
 * Vimeo product media:
 * - autoplay
 * - muted
 * - continuous loop
 * - plays inline
 * - chromeless/background player
 * - hides theme close button
 *
 * IMPORTANT:
 * This version NEVER programmatically clicks a Vimeo link.
 * If Shopify has not created the Vimeo iframe yet, the
 * iframe is safely cloned directly from Shopify's template.
 */

(function () {
  'use strict';

  const SELECTOR =
    '.product-gallery-item[data-product-media-type="external_video-vimeo"] video-component';

  function addCustomStyles() {
    if (document.getElementById('prs-product-vimeo-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'prs-product-vimeo-styles';

    style.textContent = `
      .product-gallery-item[data-product-media-type="external_video-vimeo"]
      .video-component__close,

      .product-gallery-item[data-product-media-type="external_video-vimeo"]
      [data-js-video-component-close] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }

      .product-gallery-item[data-product-media-type="external_video-vimeo"]
      iframe[src*="player.vimeo.com"] {
        width: 100% !important;
        height: 100% !important;
        border: 0 !important;
        display: block !important;
      }
    `;

    document.head.appendChild(style);
  }

  function prepareIframe(iframe) {
    if (!iframe) {
      return;
    }

    try {
      const src = iframe.getAttribute('src');

      if (!src) {
        return;
      }

      const url = new URL(src, window.location.origin);

      url.searchParams.set('autoplay', '1');
      url.searchParams.set('muted', '1');
      url.searchParams.set('loop', '1');
      url.searchParams.set('autopause', '0');
      url.searchParams.set('playsinline', '1');
      url.searchParams.set('background', '1');

      iframe.setAttribute('src', url.toString());

      iframe.setAttribute(
        'allow',
        'autoplay; fullscreen; picture-in-picture'
      );

      iframe.setAttribute('allowfullscreen', '');
    } catch (error) {
      console.warn(
        'PRS Vimeo autoplay: Could not prepare Vimeo iframe.',
        error
      );
    }
  }

  function createIframeFromTemplate(component) {
    /*
     * Do nothing if Shopify has already created the iframe.
     */
    const existingIframe = component.querySelector(
      'iframe.js-vimeo, iframe[src*="player.vimeo.com"]'
    );

    if (existingIframe) {
      prepareIframe(existingIframe);
      return;
    }

    const template = component.querySelector('template');

    if (!template || !template.content) {
      return;
    }

    const sourceIframe = template.content.querySelector(
      'iframe.js-vimeo, iframe[src*="player.vimeo.com"]'
    );

    if (!sourceIframe) {
      return;
    }

    /*
     * Clone Shopify's original Vimeo iframe rather than
     * activating it by clicking the external-video link.
     */
    const iframe = sourceIframe.cloneNode(true);

    prepareIframe(iframe);

    /*
     * Hide Shopify's poster/play link. We are replacing
     * it with the live autoplaying iframe.
     */
    const link = component.querySelector(
      '[data-js-video-component-link]'
    );

    if (link) {
      link.style.display = 'none';
      link.style.visibility = 'hidden';
      link.style.pointerEvents = 'none';
    }

    /*
     * Insert the player directly into the component.
     */
    component.appendChild(iframe);

    component.dataset.prsVimeoInjected = 'true';
  }

  function prepareVimeoComponent(component) {
    if (!component) {
      return;
    }

    /*
     * Tell the theme this is intended to autoplay.
     */
    component.setAttribute('data-autoplay', 'true');

    /*
     * Hide theme close controls.
     */
    const closeButton = component.querySelector(
      '.video-component__close, [data-js-video-component-close]'
    );

    if (closeButton) {
      closeButton.style.display = 'none';
      closeButton.style.visibility = 'hidden';
      closeButton.style.opacity = '0';
      closeButton.style.pointerEvents = 'none';
    }

    /*
     * Prepare Shopify's template iframe before insertion.
     */
    const template = component.querySelector('template');

    if (template && template.content) {
      const templateIframe = template.content.querySelector(
        'iframe.js-vimeo, iframe[src*="player.vimeo.com"]'
      );

      if (templateIframe) {
        prepareIframe(templateIframe);
      }
    }

    /*
     * If Shopify already inserted the Vimeo player, prepare it.
     * Otherwise safely instantiate it ourselves.
     */
    const liveIframe = component.querySelector(
      'iframe.js-vimeo, iframe[src*="player.vimeo.com"]'
    );

    if (liveIframe) {
      prepareIframe(liveIframe);
    } else {
      createIframeFromTemplate(component);
    }

    component.dataset.prsVimeoPrepared = 'true';
  }

  function prepareAllVimeoVideos(root) {
    const scope =
      root && root.querySelectorAll
        ? root
        : document;

    scope.querySelectorAll(SELECTOR).forEach(function (component) {
      prepareVimeoComponent(component);
    });

    if (scope.matches && scope.matches(SELECTOR)) {
      prepareVimeoComponent(scope);
    }
  }

  function init() {
    addCustomStyles();

    prepareAllVimeoVideos(document);

    /*
     * Shopify can rebuild product galleries after initial
     * page load, variant changes, slider initialisation, etc.
     */
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) {
            prepareAllVimeoVideos(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    /*
     * Re-check during the period when most Shopify themes
     * finish constructing product galleries.
     */
    [250, 600, 1200, 2000, 3500].forEach(function (delay) {
      window.setTimeout(function () {
        prepareAllVimeoVideos(document);
      }, delay);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();